/**
 * The issues list.
 *
 * URGENT FIRST, then newest — the server orders it, because "what should I
 * look at" is a property of the data rather than of this screen. The orphan
 * badge stays VISIBLE: an issue whose contact was deleted is still work
 * somebody has to finish, and hiding it would leave work nobody can find.
 */
import { useQuery } from '@apollo/client';
import {
  Badge,
  Button,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@meshhub/design-tokens/ui';
import { BarChart3, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IssueDialog } from '../../components/issues/IssueDialog.web.jsx';
import { PAGE_SIZE } from '../../constants/density.js';
import { formatCount, formatDate } from '../../lib/format.js';
import {
  ISSUE_KIND_LABELS,
  ISSUE_PRIORITY_LABELS,
  ISSUE_STATUS_LABELS,
  PRIORITY_CLASS,
  STATUS_CLASS,
} from '../../lib/issueLabels.js';
import { ISSUES_QUERY } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

const OPEN_STATUSES = ['OPEN', 'IN_PROGRESS', 'WAITING_ON_CONTACT'];

export function IssuesScreen() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statuses, setStatuses] = useState(OPEN_STATUSES);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const { data, loading, refetch } = useQuery(ISSUES_QUERY, {
    variables: {
      page,
      pageSize: PAGE_SIZE,
      statuses,
      search: search.trim() || null,
    },
  });

  const issues = data?.issues?.items ?? [];
  const totalCount = data?.issues?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="flex h-screen flex-col">
      <header
        className="flex h-toolbar shrink-0 items-center gap-4 border-b border-border
                   bg-surface px-6"
      >
        <h1 className="text-lg font-bold">{S.issues.title}</h1>
        <span className="text-sm text-text-muted">{formatCount(totalCount)}</span>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus size={14} aria-hidden="true" />
          {S.issues.newIssue}
        </Button>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={S.app.search}
          className="h-control w-64 rounded border border-border bg-bg px-3 text-base
                     outline-none focus:border-primary"
        />
        <Link to="/issues/reports" className="ms-auto flex items-center gap-2 text-sm text-primary">
          <BarChart3 size={14} aria-hidden="true" />
          {S.issues.reports}
        </Link>
      </header>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border
                      bg-surface px-6 py-2">
        {[
          { label: S.app.all, value: null },
          { label: S.issues.statusOpen, value: OPEN_STATUSES },
          { label: S.issues.statusResolved, value: ['RESOLVED'] },
          { label: S.issues.statusClosed, value: ['CLOSED'] },
        ].map((option) => (
          <button
            key={option.label}
            type="button"
            onClick={() => {
              setStatuses(option.value);
              setPage(1);
            }}
            aria-pressed={JSON.stringify(statuses) === JSON.stringify(option.value)}
            className={`rounded-full border px-3 py-1 text-sm ${
              JSON.stringify(statuses) === JSON.stringify(option.value)
                ? 'border-primary bg-primary text-primary-contrast'
                : 'border-border bg-surface text-text-muted hover:bg-surface-hover'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-6">
        {loading && issues.length === 0 && (
          <div className="flex justify-center p-7">
            <Spinner />
          </div>
        )}
        {!loading && issues.length === 0 && <EmptyState title={S.issues.noIssues} />}

        {issues.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>{S.issues.priority}</TH>
                <TH>{S.issues.issueTitle}</TH>
                <TH>{S.issues.kind}</TH>
                <TH>{S.issues.type}</TH>
                <TH>{S.contacts.title}</TH>
                <TH>{S.issues.assignee}</TH>
                <TH>{S.issues.status}</TH>
                <TH>{S.issues.reportedAt}</TH>
              </TR>
            </THead>
            <TBody>
              {issues.map((issue) => (
                <TR key={issue.id} interactive onClick={() => navigate(`/issues/${issue.id}`)}>
                  <TD className={`font-medium ${PRIORITY_CLASS[issue.priority]}`}>
                    {ISSUE_PRIORITY_LABELS[issue.priority]}
                  </TD>
                  <TD className="font-medium">{issue.title}</TD>
                  <TD className="text-text-muted">{ISSUE_KIND_LABELS[issue.kind]}</TD>
                  <TD className="text-text-muted">{issue.type?.labelFa}</TD>
                  <TD>
                    {issue.contact ? issue.contact.displayName : S.app.none}
                    {issue.orphaned && (
                      <Badge className="ms-2 bg-surface-hover text-warning">
                        {S.issues.orphaned}
                      </Badge>
                    )}
                  </TD>
                  <TD className="text-text-muted">
                    {issue.assigneeName ?? (issue.assigneeId ? issue.assigneeId : S.issues.unassigned)}
                  </TD>
                  <TD className={STATUS_CLASS[issue.status]}>
                    {ISSUE_STATUS_LABELS[issue.status]}
                  </TD>
                  <TD className="text-text-muted">{formatDate(issue.reportedAt)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      {pageCount > 1 && (
        <footer
          className="flex h-control shrink-0 items-center justify-center gap-4 border-t
                     border-border bg-surface text-sm"
        >
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="text-primary disabled:text-text-muted"
          >
            {S.app.previous}
          </button>
          <span className="text-text-muted">
            {S.app.pageOf(formatCount(page), formatCount(pageCount))}
          </span>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            className="text-primary disabled:text-text-muted"
          >
            {S.app.next}
          </button>
        </footer>
      )}

      <IssueDialog open={creating} onOpenChange={setCreating} onCreated={() => refetch()} />
    </div>
  );
}
